import { Button, Card } from 'components/atoms';
import { FormInput, LabeledStatus, ViewToggleButton } from 'components/molecules';
import ConditionalWarningPopupButton from 'components/molecules/buttons/conditional-warning-popup-button/ConditionalWarningPopupButton';
import ListWrapper from 'components/molecules/list-wrapper/ListWrapper';
import { QueryFilter, TopBar } from 'components/organisms';
import QueryFilterLabels from 'components/organisms/filters/query-form/labels/QueryFilterLabels';
import { TopbarPage } from 'components/templates';
import { useScroll } from 'components/templates/application/ApplicationTemplate';
import { useComponentVisible } from 'hooks';
import { useFormSave } from 'hooks/useFormSave';
import { useIsUserBanned } from 'hooks/useIsUserBanned';
import { useListViewById } from 'hooks/useListViewById';
import { useStoreState } from 'providers/StoreStateContext';
import { useTranslation } from 'react-i18next';
import { PiPlus } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { useGetArtistListInfinite } from 'services/artists';
import { IArtistSearch } from 'services/artists/types';
import { STATUS } from 'services/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { booleanSelectOptions, useEnumToSelectOptions } from 'utils/components/inputs/select';
import { getArtistName } from 'utils/global/name.utils';

const artistsSearchInitialValues: IArtistSearch = {
  EAN: '',
  email: '',
  externalId: '',
  externalIdType: '',
  firstName: '',
  ISRC: '',
  lastName: '',
  nickname: '',
  phoneNumber: '',
};

const Artists = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const i18nextPrefix = 'artist';

  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false);

  const searchId = 'Artists2';
  const viewId = 'artists';

  const { isBanned } = useIsUserBanned();

  const { searchById, setSearchById } = useStoreState();
  const filters = searchById[searchId] || artistsSearchInitialValues;

  const artists = useGetArtistListInfinite(filters);

  const scroll = useScroll({ fetchNextPage: artists.fetchNextPage });
  const { view } = useListViewById(viewId, 'grid');

  const { getSelectOptions } = useEnumToSelectOptions();

  const { isAnyFormSaved } = useFormSave();
  const { clearForms } = useStoreState();

  return (
    <TopbarPage
      topbarAbsolute
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title.list`)}
          path=""
          customBackItem={<div />}
          topbarContent={
            <>
              <div className="flex">
                <ViewToggleButton id={viewId} />
                <QueryFilter.ButtonOpener
                  i18nextPrefix={i18nextPrefix}
                  onClick={handleComponentVisible}
                  variant={BUTTON_VARIANT.default}
                  isClear
                />
              </div>

              {!isBanned && (
                <ConditionalWarningPopupButton
                  title={t('popup.new-form-cancelation-title')}
                  message={t('popup.new-form-cancelation-description')}
                  confirmButtonVariant={BUTTON_VARIANT.background}
                  showWarning={isAnyFormSaved}
                  onConfirm={(a) => {
                    clearForms();
                    navigate('create/data', { replace: false });
                  }}
                  onlyIcon
                  variant={BUTTON_VARIANT.submit}
                >
                  <Button.Icon icon={<PiPlus />} />
                  <Button.Description description={t('button.add')} />
                </ConditionalWarningPopupButton>
              )}
            </>
          }
        >
          <QueryFilter<IArtistSearch> i18nextPrefix={i18nextPrefix} initialValues={filters}>
            <>
              <QueryFilter.Form<IArtistSearch>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={(values) => setSearchById(searchId, values)}
              >
                <QueryFilter.InputsGrid>
                  <FormInput.Select
                    name="status"
                    label={t('input.label.status')}
                    options={getSelectOptions(STATUS, 'enum.STATUS')}
                    appBackground
                  />
                  <FormInput.Text
                    name="firstName"
                    label={t('input.label.firstName')}
                    appBackground
                  />
                  <FormInput.Text name="lastName" label={t('input.label.lastName')} appBackground />
                  <FormInput.Text name="nickname" label={t('input.label.nickname')} appBackground />
                  <FormInput.Text name="email" label="Email" type="email" appBackground />
                  <FormInput.Text
                    name="phoneNumber"
                    label={t('input.label.phoneNumber')}
                    appBackground
                  />
                  <FormInput.Text
                    name="externalIdType"
                    label={t('input.label.externalIdType')}
                    appBackground
                  />
                  <FormInput.Text
                    name="externalId"
                    label={t('input.label.externalId')}
                    appBackground
                  />
                  <FormInput.Text name="isrc" label="ISRC" appBackground />
                  <FormInput.Text name="ean" label={t('input.label.ean')} appBackground />
                  <FormInput.Select
                    appBackground
                    name="hasDuplicatedArtist"
                    label={t('input.label.hasDuplicatedArtist')}
                    options={booleanSelectOptions}
                  />
                  <FormInput.Select
                    appBackground
                    name="hasContracts"
                    label={t('input.label.hasContracts')}
                    options={booleanSelectOptions}
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilterLabels scope="artist" isFormOpen={isComponentVisible} />
            </>
          </QueryFilter>
        </TopBar>
      }
    >
      <ListWrapper
        view={view}
        items={artists.data?.pages.flat()}
        isLoading={artists.isLoading}
        isFetchingNextPage={artists.isFetchingNextPage}
        item={(artist) => (
          <Card
            view={view}
            key={artist.id}
            onClick={() => navigate(`/artist/${artist.id}/data`)}
            image={artist.profilePicturePreviewLink ?? ''}
            header={artist.nickname}
            description={getArtistName(artist.name)}
            status={<LabeledStatus status={artist.artistStatus} noname />}
          />
        )}
      />
    </TopbarPage>
  );
};

export default Artists;
